import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";

import { listMyNotifications, markNotificationRead, type NotificationItem } from "@/api/client";
import { FeedCard } from "@/components/ui/feed-card";

/**
 * "New partner registrations" panel on the staff Insights dashboard
 * (CHP-1193 / UAT AC-A-08 — Account Creation Flow A step 5's dashboard to-do).
 *
 * Renders the caller's unread `partner_registered` in-app notifications —
 * one per newly onboarded partner company, carrying the company, primary
 * contact and SPIF preference — as success-lane FeedCards. The [X] stamps
 * the notification read (`POST /notifications/{id}/read`); the table is
 * append-only, so read is the only mutation. Renders nothing when there's
 * nothing unread — the dashboard stays clean between registrations.
 */
export function NewPartnerNotifications({ token }: { token: string }) {
  const queryClient = useQueryClient();

  const notifications = useQuery<NotificationItem[]>({
    queryKey: ["notifications", "me", "unread"],
    queryFn: () => listMyNotifications(token, { unreadOnly: true }),
    enabled: !!token,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(token, id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const cards = (notifications.data ?? []).filter((n) => n.type === "partner_registered");
  if (cards.length === 0) return null;

  const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

  return (
    <section className="mt-6" aria-label="New partner registrations">
      <h2 className="text-lg font-semibold tracking-tight">New partner registrations</h2>
      <div className="mt-3 space-y-3">
        {cards.map((n) => {
          const company = text(n.payload.company_name) ?? "New partner";
          const contact = text(n.payload.contact_name);
          const email = text(n.payload.contact_email);
          const spif = text(n.payload.spif_preference);
          return (
            <FeedCard
              key={n.id}
              tone="success"
              icon={UserPlus}
              eyebrow="New partner onboarded"
              title={company}
              rawTestId={`partner-registered-${n.id}`}
              onDismiss={() => markRead.mutate(n.id)}
              // NOT "Dismiss notification for …" — that phrasing collides with
              // the deal-feed test matchers (/dismiss notification for …/i).
              dismissLabel={`Mark ${company} registration as read`}
              dismissTestId={`partner-registered-read-${n.id}`}
              description={
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {contact ? <span>{contact}</span> : null}
                  {email ? <span>{email}</span> : null}
                  {spif ? <span>SPIF: {spif}</span> : null}
                </div>
              }
            />
          );
        })}
      </div>
    </section>
  );
}
